/**
 * Utility helpers for API Error.
 * This file contains shared pure helpers used by multiple screens, services, or wrappers.
 */
import { HttpErrorResponse } from '@angular/common/http';

const NETWORK_ERROR_MESSAGE = 'Something went wrong. Please check your internet connection and try again.';
const OFFLINE_ERROR_MESSAGE = 'Something went wrong. Please check your internet connection and try again.';
const SERVER_ERROR_MESSAGE = 'Something went wrong. Please try again.';

export function getApiErrorMessage(err: unknown, fallback = 'An unexpected error occurred.'): string {
  if (isOffline()) {
    return OFFLINE_ERROR_MESSAGE;
  }

  if (err instanceof HttpErrorResponse) {
    const responseMessage = extractMessageValue(err.error);
    if (responseMessage) return responseMessage;

    if (err.status === 0) {
      return NETWORK_ERROR_MESSAGE;
    }

    if (err.status >= 500) {
      return SERVER_ERROR_MESSAGE;
    }
  }

  const responseMessage = extractResponseMessage(err);
  if (responseMessage) return responseMessage;

  if (err instanceof Error && err.message) {
    return normalizeBrowserErrorMessage(err.message, fallback);
  }

  if (typeof err === 'string' && err.trim()) {
    return err.trim();
  }

  return fallback;
}

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

function extractResponseMessage(err: unknown): string {
  if (!err || typeof err !== 'object') return '';

  const errorBody = (err as { error?: unknown }).error;
  const bodyMessage = extractMessageValue(errorBody);
  if (bodyMessage) return bodyMessage;

  return extractMessageValue(err);
}

function extractMessageValue(value: unknown): string {
  if (typeof value === 'string') {
    const message = value.trim();
    return isHtmlErrorPage(message) ? '' : message;
  }
  if (Array.isArray(value)) {
    return value.map((item) => extractMessageValue(item)).filter(Boolean).join(' ');
  }
  if (!value || typeof value !== 'object') return '';

  const record = value as Record<string, unknown>;
  for (const key of ['message', 'error', 'detail', 'title']) {
    const message = extractMessageValue(record[key]);
    if (message) return message;
  }

  return '';
}

function normalizeBrowserErrorMessage(message: string, fallback: string): string {
  const normalized = message.trim();
  if (!normalized) return fallback;
  if (isHtmlErrorPage(normalized)) return SERVER_ERROR_MESSAGE;

  const lower = normalized.toLowerCase();
  if (lower.includes('failed to fetch') || lower.includes('networkerror') || lower.includes('load failed')) {
    return NETWORK_ERROR_MESSAGE;
  }

  return normalized;
}

function isHtmlErrorPage(value: string): boolean {
  const lower = value.trimStart().toLowerCase();
  return lower.startsWith('<!doctype html') || lower.startsWith('<html') || /<\/?[a-z][\s\S]*>/i.test(value);
}
